import React, { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { format, subDays, eachDayOfInterval, parseISO, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiBarChart3, FiTrendingUp, FiTrendingDown, FiCalendar, FiAward, FiTarget, FiUsers, FiClock } = FiIcons;

const Analytics = () => {
  const { children, sleep_logs, earned_rewards, rewards, getChildStats } = useApp();
  const [timeRange, setTimeRange] = useState('30'); // 7, 30, 90 days
  const [selectedChild, setSelectedChild] = useState('all');

  const timeRangeOptions = [
    { value: '7', label: 'Last 7 Days' },
    { value: '30', label: 'Last 30 Days' },
    { value: '90', label: 'Last 90 Days' }
  ];

  // Calculate analytics data
  const analyticsData = useMemo(() => {
    const days = parseInt(timeRange);
    const endDate = new Date();
    const startDate = subDays(endDate, days - 1);
    const dateRange = eachDayOfInterval({ start: startDate, end: endDate });

    const filteredLogs = sleep_logs.filter(log => {
      const logDate = parseISO(log.date);
      const inRange = logDate >= startDate && logDate <= endDate;
      const childMatch = selectedChild === 'all' || log.childId === parseInt(selectedChild);
      return inRange && childMatch;
    });

    // Family overview stats
    const totalNights = filteredLogs.length;
    const goodNights = filteredLogs.filter(log => log.good).length;
    const successRate = totalNights > 0 ? Math.round((goodNights / totalNights) * 100) : 0;

    // Daily success rates for chart
    const dailyData = dateRange.map(date => {
      const dateStr = format(date, 'yyyy-MM-dd');
      const dayLogs = filteredLogs.filter(log => log.date === dateStr);
      const dayGoodNights = dayLogs.filter(log => log.good).length;
      const dayTotal = dayLogs.length;
      const daySuccessRate = dayTotal > 0 ? Math.round((dayGoodNights / dayTotal) * 100) : null;
      
      return {
        date: dateStr,
        displayDate: format(date, 'MMM d'),
        successRate: daySuccessRate,
        goodNights: dayGoodNights,
        totalNights: dayTotal
      };
    });

    // Child comparison data
    const childrenData = children.map(child => {
      const childLogs = filteredLogs.filter(log => log.childId === child.id);
      const childGoodNights = childLogs.filter(log => log.good).length;
      const childTotal = childLogs.length;
      const childSuccessRate = childTotal > 0 ? Math.round((childGoodNights / childTotal) * 100) : 0;
      const stats = getChildStats(child.id);
      
      return {
        ...child,
        successRate: childSuccessRate,
        goodNights: childGoodNights,
        totalNights: childTotal,
        currentStreak: stats.currentStreak,
        bestStreak: stats.bestStreak,
        totalGoodNightsEver: stats.totalGoodNights
      };
    });

    // Rewards analytics
    const rewardsInPeriod = earned_rewards.filter(reward => {
      const earnedDate = parseISO(reward.earnedOn);
      const inRange = earnedDate >= startDate && earnedDate <= endDate;
      const childMatch = selectedChild === 'all' || reward.childId === parseInt(selectedChild);
      return inRange && childMatch;
    });

    return {
      totalNights,
      goodNights,
      successRate,
      dailyData,
      childrenData,
      rewardsEarned: rewardsInPeriod.length,
      averageStreak: childrenData.length > 0 
        ? Math.round(childrenData.reduce((sum, child) => sum + child.currentStreak, 0) / childrenData.length)
        : 0
    };
  }, [timeRange, selectedChild, children, sleep_logs, earned_rewards, getChildStats]);

  const getSuccessRateColor = (rate) => {
    if (rate >= 80) return 'text-green-600 bg-green-50';
    if (rate >= 60) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getSuccessRateBarColor = (rate) => {
    if (rate >= 80) return 'bg-green-500';
    if (rate >= 60) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  if (children.length === 0) {
    return (
      <div className="text-center py-12">
        <SafeIcon icon={FiBarChart3} className="text-6xl text-gray-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-700 mb-2">No Analytics Available</h2>
        <p className="text-gray-500">Add children and start logging sleep data to see analytics!</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center space-x-3">
            <SafeIcon icon={FiBarChart3} className="text-purple-600" />
            <span>Family Sleep Analytics</span>
          </h1>
          <p className="text-gray-600 mt-1">Track progress and compare sleep patterns across your family</p>
        </div>

        <div className="flex space-x-3">
          <select
            value={selectedChild}
            onChange={(e) => setSelectedChild(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">All Children</option>
            {children.map(child => (
              <option key={child.id} value={child.id}>
                {child.avatarEmoji} {child.name}
              </option>
            ))}
          </select>

          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            {timeRangeOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Success Rate</p>
              <p className="text-2xl font-bold text-gray-900">{analyticsData.successRate}%</p>
            </div>
            <div className={`p-3 rounded-full ${getSuccessRateColor(analyticsData.successRate)}`}>
              <SafeIcon icon={analyticsData.successRate >= 80 ? FiTrendingUp : FiTrendingDown} className="text-xl" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {analyticsData.goodNights} of {analyticsData.totalNights} nights
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Good Nights</p>
              <p className="text-2xl font-bold text-gray-900">{analyticsData.goodNights}</p>
            </div>
            <div className="p-3 rounded-full bg-green-50 text-green-600">
              <SafeIcon icon={FiCalendar} className="text-xl" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            In the last {timeRange} days
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Rewards Earned</p>
              <p className="text-2xl font-bold text-gray-900">{analyticsData.rewardsEarned}</p>
            </div>
            <div className="p-3 rounded-full bg-yellow-50 text-yellow-600">
              <SafeIcon icon={FiAward} className="text-xl" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Total rewards unlocked
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Average Streak</p>
              <p className="text-2xl font-bold text-gray-900">{analyticsData.averageStreak}</p>
            </div>
            <div className="p-3 rounded-full bg-purple-50 text-purple-600">
              <SafeIcon icon={FiTarget} className="text-xl" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Current streak average
          </p>
        </div>
      </div>

      {/* Daily Success Rate Chart */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-6">Daily Success Rate</h3>
        <div className="space-y-3">
          {analyticsData.dailyData.slice(-14).map((day, index) => (
            <div key={day.date} className="flex items-center space-x-3">
              <div className="w-16 text-xs text-gray-600 text-right">
                {day.displayDate}
              </div>
              <div className="flex-1 bg-gray-200 rounded-full h-6 relative">
                {day.successRate !== null && (
                  <div
                    className={`h-6 rounded-full ${getSuccessRateBarColor(day.successRate)} transition-all duration-500`}
                    style={{ width: `${day.successRate}%` }}
                  ></div>
                )}
                <div className="absolute inset-0 flex items-center justify-center text-xs font-medium text-gray-700">
                  {day.successRate !== null ? `${day.successRate}%` : 'No data'}
                </div>
              </div>
              <div className="w-16 text-xs text-gray-600">
                {day.goodNights}/{day.totalNights}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Child Comparison */}
      {selectedChild === 'all' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center space-x-2">
            <SafeIcon icon={FiUsers} />
            <span>Child Comparison</span>
          </h3>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {analyticsData.childrenData.map((child) => (
              <div key={child.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="text-2xl">{child.avatarEmoji}</div>
                  <div>
                    <h4 className="font-semibold text-gray-800">{child.name}</h4>
                    <p className="text-sm text-gray-500">Age {child.age}</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Success Rate</span>
                    <span className={`px-2 py-1 rounded text-sm font-medium ${getSuccessRateColor(child.successRate)}`}>
                      {child.successRate}%
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Current Streak</span>
                    <span className="text-sm font-medium text-purple-600">
                      🔥 {child.currentStreak}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Best Streak</span>
                    <span className="text-sm font-medium text-orange-600">
                      🏆 {child.bestStreak}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Good Nights</span>
                    <span className="text-sm font-medium text-green-600">
                      {child.goodNights}/{child.totalNights}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Insights */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border border-purple-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center space-x-2">
          <SafeIcon icon={FiClock} />
          <span>Insights & Recommendations</span>
        </h3>
        
        <div className="space-y-3 text-sm">
          {analyticsData.successRate >= 80 && (
            <div className="flex items-start space-x-2 text-green-700">
              <SafeIcon icon={FiTrendingUp} className="text-green-500 mt-0.5" />
              <p>Excellent work! Your family is maintaining a great bedtime routine with an {analyticsData.successRate}% success rate.</p>
            </div>
          )}
          
          {analyticsData.successRate < 60 && (
            <div className="flex items-start space-x-2 text-orange-700">
              <SafeIcon icon={FiTarget} className="text-orange-500 mt-0.5" />
              <p>Consider reviewing bedtime routines. A consistent schedule and reward system can help improve success rates.</p>
            </div>
          )}
          
          {analyticsData.averageStreak < 3 && analyticsData.childrenData.length > 0 && (
            <div className="flex items-start space-x-2 text-blue-700">
              <SafeIcon icon={FiAward} className="text-blue-500 mt-0.5" />
              <p>Setting up more frequent milestone rewards might help motivate longer streaks.</p>
            </div>
          )}
          
          {analyticsData.rewardsEarned > 5 && (
            <div className="flex items-start space-x-2 text-purple-700">
              <SafeIcon icon={FiAward} className="text-purple-500 mt-0.5" />
              <p>Great job! {analyticsData.rewardsEarned} rewards earned shows the system is working well for your family.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Analytics;